import {
	createContext,
	ReactNode,
	use,
	useEffect,
	useLayoutEffect,
} from 'react';
import { shallow } from 'zustand/shallow';

import { useWidgetConfigStore, type WidgetConfigState } from './store';

type WidgetConfigProviderProps = {
	children: ReactNode;
	baseApiUrl: string;
	widgetApiUrl: string;
	interactionUrl: string;
	imagePath: string;
	loginUrl: string;
	APIEndPoints?: { [key: string]: string };
	googleMapsApiKey?: string;
};

const WidgetConfigContext = createContext<WidgetConfigState | null>(null);

const selectWholeConfig = (state: WidgetConfigState) => state;

export function WidgetConfigProvider({
	baseApiUrl,
	widgetApiUrl,
	interactionUrl,
	imagePath,
	loginUrl,
	children,
	APIEndPoints,
	googleMapsApiKey,
}: WidgetConfigProviderProps) {
	const hydrate = useWidgetConfigStore((state) => state.hydrate);
	const setDisableLive = useWidgetConfigStore(
		(state) => state.setDisableLive
	);
	// Shallow-compared so a hydrate() call that changes nothing (the store
	// already bails out internally, but a defensive belt-and-braces here
	// too) doesn't push a new context value to every consumer under
	// WidgetConfigProvider — which wraps the entire app.
	const config = useWidgetConfigStore(selectWholeConfig, shallow);

	useLayoutEffect(() => {
		hydrate({
			baseApiUrl,
			widgetApiUrl,
			interactionUrl,
			imagePath,
			loginUrl,
			APIEndPoints,
			googleMapsApiKey,
		});
	}, [
		hydrate,
		baseApiUrl,
		widgetApiUrl,
		interactionUrl,
		imagePath,
		loginUrl,
		APIEndPoints,
		googleMapsApiKey,
	]);

	useEffect(() => {
		if (/(widget\/embed|\/embed)/.test(window.location.pathname)) {
			const searchParams = new URLSearchParams(window.location.search);
			if (searchParams.get('disableLive')) {
				setDisableLive(searchParams.get('disableLive') === 'true');
			}
		}
	}, [setDisableLive]);

	return (
		<WidgetConfigContext.Provider value={config}>
			{children}
		</WidgetConfigContext.Provider>
	);
}

export function useWidgetConfigContext() {
	const value = use(WidgetConfigContext);
	if (value) {
		return value;
	}
	// Defensive-only: WidgetConfigProvider always wraps the app tree
	// (_app.tsx), so this path is not expected to run in production. It's a
	// non-reactive snapshot rather than a store subscription so that adding
	// it doesn't give every one of this hook's ~30 call sites a direct
	// subscription to the whole config store (the context above already
	// provides that, shallow-compared, in the normal path).
	return useWidgetConfigStore.getState();
}
